import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PaymentInstructions } from '@/components/PaymentInstructions';
import { useSeason } from '@/contexts/SeasonContext';
import { cn } from '@/lib/utils';

export default function Rules({ me }) {
  const { entryFee } = useSeason();
  const feeLabel =
    entryFee != null && Number.isFinite(Number(entryFee))
      ? `$${Number(entryFee)} per entry`
      : 'Set by the league admin';

  return (
    <div className="mx-auto max-w-7xl space-y-4 px-4 py-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">League Information</h1>
        <h2 className="text-lg font-medium text-muted-foreground">
          Welcome to the South Boston Challenge Club&apos;s Survivor league!
        </h2>
      </div>

      <InfoSection title="League History" index={0}>
        <div className="space-y-3 leading-relaxed">
          <p>
            Our site&apos;s &quot;Tears&quot; nickname comes from historical results being made
            available on Tuesday mornings — the defeated discovering their losses and exiting
            the season, Tuesday morning tears in tow.
          </p>
          <p>
            This app will now provide instantaneous results; players can witness their demise
            in real time (and shed those tears immediately).
          </p>
          <p>
            Though the site will automate many processes, I will still administer and track
            down stragglers every week. If you&apos;re someone who needs that extra reminder,
            be sure to add your preferred contact info when you register!
          </p>
        </div>
      </InfoSection>

      <InfoSection title="Enter" index={1}>
        <ul className="list-disc space-y-2 pl-5 leading-relaxed">
          <li>
            <Link to="/auth" className="text-primary underline-offset-4 hover:underline">
              Register an account
            </Link>{' '}
            with your email and cell
          </li>
          <li>
            <u><i>Create</i></u> and <u><i>name</i></u> your entries
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Please keep names family-friendly!</li>
              <li>No limit on number of entries per player</li>
              <li>{feeLabel}</li>
            </ul>
          </li>
          <li>
            <strong>Prizes:</strong> <u>100% of entries</u> are returned to participants as prizes
          </li>
        </ul>
      </InfoSection>

      <InfoSection title="Pick" index={2}>
        <ul className="list-disc space-y-2 pl-5 leading-relaxed">
          <li>
            Every week each entry picks one team to win straight up
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Game spread does not matter!</li>
            </ul>
          </li>
          <li>An entry can pick a team only once per season</li>
          <li>A win progresses to the next week</li>
          <li>
            A loss is eliminated from the pool
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>A game ending in a tie is a loss</li>
              <li>A game cancellation is a loss</li>
              <li>A game postponed to a later week is a loss</li>
            </ul>
          </li>
        </ul>
      </InfoSection>

      <InfoSection title="Win" index={3}>
        <ul className="list-disc space-y-2 pl-5 leading-relaxed">
          <li>The last pick surviving secures 1st place</li>
          <li>
            If all live picks vying for the same prize lose in the same week, all are graced
            into the following week.
          </li>
          <li>
            Multiple picks surviving through and tying at the end of the season will share
            remaining earned prizes equally
          </li>
        </ul>
      </InfoSection>

      <InfoSection title="Minutia" index={4}>
        <ul className="list-disc space-y-2 pl-5 leading-relaxed">
          <li>Picks must be submitted before the general deadline: 12PM ET Sundays</li>
          <li>Picks for games prior to the deadline are due before their kickoff</li>
          <li>Thursday Night games are not eligible. This includes Thanksgiving!</li>
          <li>
            Late picks will be defaulted to the underdog in the matchup with the smallest
            available spread greater than 6 points (one TD).
          </li>
          <li>
            Post-deadline, all picks are locked regardless of injury reports, roster changes,
            acts of God, etc.
          </li>
        </ul>
      </InfoSection>

      <InfoSection title="Playoffs (Week 18 / Sudden Death)" index={5}>
        <ul className="list-disc space-y-2 pl-5 leading-relaxed">
          <li>All picks surviving beyond Week 17 will have their team selections reset</li>
          <li>Each pick must choose a winner in every game for the week</li>
          <li>Each pick must assign each game a point value from 1 through 16</li>
          <li>Each point value can be assigned to only one game</li>
          <li>
            E.g., if you believe NE over BUF is the lock of the week, your first pick would
            be NE - 16. Values of 1 through 15 can be assigned to the remaining fifteen games.
          </li>
          <li>Each win receives its given point value</li>
          <li>Final standings are determined by total points; most points wins</li>
        </ul>
      </InfoSection>

      <InfoSection title="Fund Your Entries" index={6}>
        {me ? (
          <PaymentInstructions entryFee={entryFee} />
        ) : (
          <div className="space-y-2 leading-relaxed">
            {entryFee != null && Number.isFinite(Number(entryFee)) && (
              <p>
                Entry fee: <strong>${Number(entryFee)}</strong> per entry.
              </p>
            )}
            <p>
              <Link to="/auth" className="text-primary underline-offset-4 hover:underline">
                Log in
              </Link>{' '}
              to see payment details.
            </p>
          </div>
        )}
      </InfoSection>
    </div>
  );
}

function InfoSection({ title, index = 0, children }) {
  const [open, setOpen] = useState(true);

  return (
    <Card className={index % 2 === 1 ? 'bg-secondary' : 'bg-background'}>
      <CardHeader className="pb-3">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 text-left"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          <CardTitle>{title}</CardTitle>
          <ChevronDown
            className={cn(
              'h-5 w-5 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180'
            )}
            aria-hidden
          />
        </button>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
}
